import { c as _c } from "react/compiler-runtime";
import { IcoEyeCrossed16 } from '@onefootprint/icons';
import type { AgentKind, UserTimelineEventAgentExecutionStarted } from '@onefootprint/request-types/dashboard';
import { LinkButton, Tooltip } from '@onefootprint/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AGENT_ICONS, { AGENT_ICON_COMPONENTS, DEFAULT_AGENT_ICON } from '@/components/agent-icon-picker/agent-icons';
import ShimmerText from '@/components/shimmer-text';
import { agentBuilderUrl } from '@/config/constants';
import useSession from '@/hooks/use-session';
type AgentExecutionStartedEventData = UserTimelineEventAgentExecutionStarted['data'];
type AgentExecutionStartedEventHeaderProps = {
  data: AgentExecutionStartedEventData;
};
export const SHOW_INVESTIGATION_KINDS: AgentKind[] = ['decision', 'copilot'];
const SUBTITLE_INTERVAL_MS = 5000;

/**
 * Failed and abandoned (deactivated) runs are internal detail: everyone sees pending and completed
 * runs, but only firm employees see these. Single source of truth, shared by the visibility gate and
 * the "employee only" marker on the row.
 */
const isEmployeeOnly = (data: AgentExecutionStartedEventData) => data.state === 'error' || data.isDeactivated;
export const isAgentExecutionStartedVisible = (data: AgentExecutionStartedEventData, isFirmEmployee: boolean) => isFirmEmployee || !isEmployeeOnly(data);
const AgentExecutionStartedEventHeader = t0 => {
  const $ = _c(104);
  const {
    data
  } = t0;
  const {
    data: session
  } = useSession();
  const isFirmEmployee = !!session.user?.isFirmEmployee;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "auditTrail.timeline.agentExecutionStartedEvent"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const {
    state,
    isDeactivated,
    agentIcon,
    agentName,
    agentId,
    agentVersionId,
    agentVersionTag,
    agentKind,
    investigationId,
    errorReason
  } = data;
  let t2;
  if ($[1] !== agentName || $[2] !== agentVersionTag || $[3] !== t) {
    const name = agentName ?? t(_temp);
    t2 = agentVersionTag ? t(_temp2, {
      name,
      version: agentVersionTag
    }) : name;
    $[1] = agentName;
    $[2] = agentVersionTag;
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const agentLabel = t2;
  const iconKey = AGENT_ICONS.find(i => i === agentIcon) ?? DEFAULT_AGENT_ICON;
  const Icon = AGENT_ICON_COMPONENTS[iconKey];
  const isRunning = state === "pending" && !isDeactivated;
  const isFailed = state === "error";
  const investigationLink = investigationId && SHOW_INVESTIGATION_KINDS.includes(agentKind) ? `/investigations/${investigationId}` : undefined;
  let t3;
  if ($[5] !== t) {
    t3 = t(_temp3);
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t) {
    t4 = t(_temp4);
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t(_temp5);
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t) {
    t6 = t(_temp6);
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t) {
    t7 = t(_temp7);
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t) {
    t8 = t(_temp8);
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t) {
    t9 = t(_temp9);
    $[17] = t;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t) {
    t10 = t(_temp10);
    $[19] = t;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t) {
    t11 = t(_temp11);
    $[21] = t;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t3 || $[26] !== t4 || $[27] !== t5 || $[28] !== t6 || $[29] !== t7 || $[30] !== t8 || $[31] !== t9) {
    t12 = [t3, t4, t5, t6, t7, t8, t9, t10, t11];
    $[23] = t10;
    $[24] = t11;
    $[25] = t3;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const subtitles = t12;
  let t13;
  if ($[33] !== subtitles) {
    t13 = () => subtitles[Math.floor(Math.random() * subtitles.length)];
    $[33] = subtitles;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const [subtitle, setSubtitle] = useState(t13);
  let t14;
  let t15;
  if ($[35] !== isRunning || $[36] !== subtitles) {
    t14 = () => {
      if (!isRunning) {
        return;
      }
      const interval = setInterval(() => {
        setSubtitle(subtitles[Math.floor(Math.random() * subtitles.length)]);
      }, SUBTITLE_INTERVAL_MS);
      return () => clearInterval(interval);
    };
    t15 = [isRunning, subtitles];
    $[35] = isRunning;
    $[36] = subtitles;
    $[37] = t14;
    $[38] = t15;
  } else {
    t14 = $[37];
    t15 = $[38];
  }
  useEffect(t14, t15);
  if (!isAgentExecutionStartedVisible(data, isFirmEmployee)) {
    return null;
  }
  if (isRunning) {
    let t16;
    if ($[39] !== Icon) {
      t16 = <div className="flex items-center size-4 shrink-0 [&>svg]:size-4"><Icon /></div>;
      $[39] = Icon;
      $[40] = t16;
    } else {
      t16 = $[40];
    }
    let t17;
    if ($[41] !== agentLabel) {
      t17 = <span className="text-label-2 text-primary">{agentLabel}</span>;
      $[41] = agentLabel;
      $[42] = t17;
    } else {
      t17 = $[42];
    }
    let t18;
    if ($[43] !== t) {
      t18 = t(_temp12);
      $[43] = t;
      $[44] = t18;
    } else {
      t18 = $[44];
    }
    let t19;
    if ($[45] !== t18) {
      t19 = <span className="text-body-2 text-tertiary">{t18}</span>;
      $[45] = t18;
      $[46] = t19;
    } else {
      t19 = $[46];
    }
    let t20;
    if ($[47] !== subtitle) {
      t20 = <span className="text-body-2 text-tertiary">{subtitle}</span>;
      $[47] = subtitle;
      $[48] = t20;
    } else {
      t20 = $[48];
    }
    let t21;
    if ($[49] !== t16 || $[50] !== t17 || $[51] !== t19 || $[52] !== t20) {
      t21 = <ShimmerText className="gap-1 inline-flex flex-wrap items-center md:flex-nowrap">{t16}{t17}{t19}{t20}</ShimmerText>;
      $[49] = t16;
      $[50] = t17;
      $[51] = t19;
      $[52] = t20;
      $[53] = t21;
    } else {
      t21 = $[53];
    }
    let t22;
    if ($[54] !== investigationLink || $[55] !== t) {
      t22 = investigationLink && <LinkButton href={investigationLink} target="_blank">{t(_temp13)}</LinkButton>;
      $[54] = investigationLink;
      $[55] = t;
      $[56] = t22;
    } else {
      t22 = $[56];
    }
    let t23;
    if ($[57] !== t21 || $[58] !== t22) {
      t23 = <div className="flex flex-wrap items-center gap-1 md:flex-nowrap">{t21}{t22}</div>;
      $[57] = t21;
      $[58] = t22;
      $[59] = t23;
    } else {
      t23 = $[59];
    }
    return t23;
  }
  let t16;
  if ($[60] !== t) {
    t16 = t(_temp14);
    $[60] = t;
    $[61] = t16;
  } else {
    t16 = $[61];
  }
  let t17;
  if ($[62] !== t) {
    t17 = t(_temp15);
    $[62] = t;
    $[63] = t17;
  } else {
    t17 = $[63];
  }
  let t18;
  if ($[64] !== t) {
    t18 = t(_temp16);
    $[64] = t;
    $[65] = t18;
  } else {
    t18 = $[65];
  }
  let t19;
  if ($[66] !== t) {
    t19 = t(_temp17);
    $[66] = t;
    $[67] = t19;
  } else {
    t19 = $[67];
  }
  let t20;
  if ($[68] !== t) {
    t20 = t(_temp18);
    $[68] = t;
    $[69] = t20;
  } else {
    t20 = $[69];
  }
  let t21;
  if ($[70] !== t) {
    t21 = t(_temp19);
    $[70] = t;
    $[71] = t21;
  } else {
    t21 = $[71];
  }
  let t22;
  if ($[72] !== t16 || $[73] !== t17 || $[74] !== t18 || $[75] !== t19 || $[76] !== t20 || $[77] !== t21) {
    t22 = {
      watchlist_review: t16,
      document_extraction: t17,
      research: t18,
      decision: t19,
      document_verification: t20,
      copilot: t21
    };
    $[72] = t16;
    $[73] = t17;
    $[74] = t18;
    $[75] = t19;
    $[76] = t20;
    $[77] = t21;
    $[78] = t22;
  } else {
    t22 = $[78];
  }
  const kindToDescription = t22;
  let t23;
  if ($[79] !== data || $[80] !== t) {
    t23 = isEmployeeOnly(data) && <Tooltip content={t(_temp20)} side="left"><span className="flex items-center shrink-0"><IcoEyeCrossed16 color="tertiary" /></span></Tooltip>;
    $[79] = data;
    $[80] = t;
    $[81] = t23;
  } else {
    t23 = $[81];
  }
  let t24;
  if ($[82] !== Icon) {
    t24 = <div className="flex items-center size-4 shrink-0 [&>svg]:size-4"><Icon /></div>;
    $[82] = Icon;
    $[83] = t24;
  } else {
    t24 = $[83];
  }
  let t25;
  if ($[84] !== agentId || $[85] !== agentLabel || $[86] !== agentVersionId) {
    t25 = agentId ? <LinkButton href={agentBuilderUrl(agentId, {
      versionId: agentVersionId
    })} className="text-label-2">{agentLabel}</LinkButton> : <span className="text-label-2 text-primary">{agentLabel}</span>;
    $[84] = agentId;
    $[85] = agentLabel;
    $[86] = agentVersionId;
    $[87] = t25;
  } else {
    t25 = $[87];
  }
  let t26;
  if ($[88] !== agentKind || $[89] !== errorReason || $[90] !== isFailed || $[91] !== kindToDescription || $[92] !== t) {
    t26 = isFailed ? <span className="text-body-2 text-tertiary">{errorReason ? t($_19 => $_19.errorReason[errorReason]) : t(_temp21)}</span> : <span className="text-body-2 text-tertiary">{kindToDescription[agentKind]}</span>;
    $[88] = agentKind;
    $[89] = errorReason;
    $[90] = isFailed;
    $[91] = kindToDescription;
    $[92] = t;
    $[93] = t26;
  } else {
    t26 = $[93];
  }
  let t27;
  if ($[94] !== investigationLink || $[95] !== isFailed || $[96] !== t) {
    t27 = isFailed && investigationLink && <><span className="text-body-2 text-tertiary">·</span><LinkButton href={investigationLink} target="_blank">{t(_temp22)}</LinkButton></>;
    $[94] = investigationLink;
    $[95] = isFailed;
    $[96] = t;
    $[97] = t27;
  } else {
    t27 = $[97];
  }
  let t28;
  if ($[98] !== t23 || $[99] !== t24 || $[100] !== t25 || $[101] !== t26 || $[102] !== t27) {
    t28 = <div className="flex flex-wrap items-center gap-1 md:flex-nowrap">{t23}{t24}{t25}{t26}{t27}</div>;
    $[98] = t23;
    $[99] = t24;
    $[100] = t25;
    $[101] = t26;
    $[102] = t27;
    $[103] = t28;
  } else {
    t28 = $[103];
  }
  return t28;
};
export default AgentExecutionStartedEventHeader;
function _temp($) {
  return $.defaultAgentName;
}
function _temp2($_0) {
  return $_0.agentWithVersion;
}
function _temp3($_1) {
  return $_1.subtitles.runningAnalysis;
}
function _temp4($_2) {
  return $_2.subtitles.evaluatingResults;
}
function _temp5($_3) {
  return $_3.subtitles.processingData;
}
function _temp6($_4) {
  return $_4.subtitles.reviewingInformation;
}
function _temp7($_5) {
  return $_5.subtitles.performingChecks;
}
function _temp8($_6) {
  return $_6.subtitles.analyzingInputs;
}
function _temp9($_7) {
  return $_7.subtitles.runningEvaluation;
}
function _temp10($_8) {
  return $_8.subtitles.assessingData;
}
function _temp11($_9) {
  return $_9.subtitles.examiningRecords;
}
function _temp12($_10) {
  return $_10.is;
}
function _temp13($_11) {
  return $_11.viewProgress;
}
function _temp14($_12) {
  return $_12.description.watchlistReview;
}
function _temp15($_13) {
  return $_13.description.documentExtraction;
}
function _temp16($_14) {
  return $_14.description.research;
}
function _temp17($_15) {
  return $_15.description.decision;
}
function _temp18($_16) {
  return $_16.description.documentVerification;
}
function _temp19($_17) {
  return $_17.description.copilot;
}
function _temp20($_18) {
  return $_18.employeeOnly;
}
function _temp21($_20) {
  return $_20.failedLabel;
}
function _temp22($_21) {
  return $_21.viewInvestigation;
}